import { Alert, Badge, Button, Card, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useHandlers, useUser } from '../../../../context/page-context';
import { useConfirmedPasswordStatus } from '../hooks/use-confirmed-password-status';
import { useEnableTwoFactor } from '../hooks/use-enable-two-factor';
import { usePasswordConfirmationGuard } from '../hooks/use-password-confirmation-guard';
import { useRecoveryCodes } from '../hooks/use-recovery-codes';
import { useRegenerateRecoveryCodes } from '../hooks/use-regenerate-recovery-codes';
import { useResetTwoFactor } from '../hooks/use-reset-two-factor';
import { ConfirmPasswordModal } from './confirm-password-modal';
import { EnableTwoFactorFlow } from './enable-two-factor-flow';
import { RecoveryCodesCard } from './recovery-codes-card';

type View = 'not-enrolled' | 'enrolling' | 'enrolled';

export function TwoFactorStatusCard() {
  const { twoFactorEnabled, twoFactorRequired } = useUser();
  const { notificationHandler } = useHandlers();

  const [view, setView] = useState<View>(twoFactorEnabled ? 'enrolled' : 'not-enrolled');
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  // Distinguishes "enrolling for the first time" from "replacing a working device": the copy
  // differs, and a reset needs the warning that the old authenticator is already dead.
  const [isResetting, setIsResetting] = useState(false);

  const passwordStatus = useConfirmedPasswordStatus();
  const guard = usePasswordConfirmationGuard();

  const enableTwoFactor = useEnableTwoFactor();
  const resetTwoFactor = useResetTwoFactor();
  const regenerateRecoveryCodes = useRegenerateRecoveryCodes();
  const recoveryCodes = useRecoveryCodes(showRecoveryCodes);

  function runGuarded(action: () => void) {
    guard.run(!!passwordStatus.data?.confirmed, action);
  }

  function handleEnableClick() {
    runGuarded(() => {
      enableTwoFactor.mutate(undefined, {
        onSuccess: () => setView('enrolling'),
        onError: (error) => {
          if (!guard.retryIfRequired(error, handleEnableClick)) {
            notificationHandler.error(
              t('features:account.components.two_factor_status_card.notifications.enable_failed'),
            );
          }
        },
      });
    });
  }

  function handleResetClick() {
    runGuarded(() => {
      resetTwoFactor.mutate(undefined, {
        onSuccess: () => {
          setIsResetting(true);
          setView('enrolling');
        },
        onError: (error) => {
          if (!guard.retryIfRequired(error, handleResetClick)) {
            notificationHandler.error(
              t('features:account.components.two_factor_status_card.notifications.reset_failed'),
            );
          }
        },
      });
    });
  }

  function handleCancelEnrolling() {
    // Always 'not-enrolled': a reset has already cleared the old factor server-side, so the
    // account really is unenrolled until the new secret is confirmed. Claiming otherwise would
    // show "Enabled" for an account that logs in on a password alone.
    setView('not-enrolled');
    setIsResetting(false);
  }

  function handleRevealRecoveryCodes() {
    runGuarded(() => setShowRecoveryCodes(true));
  }

  function handleRetryRecoveryCodes() {
    // The deferred action is a bare refetch, not this handler - retrying through the handler
    // would re-read the same stale 423 and re-open the modal forever.
    if (guard.retryIfRequired(recoveryCodes.error, () => void recoveryCodes.refetch())) {
      return;
    }
    void recoveryCodes.refetch();
  }

  function handleRegenerateRecoveryCodes() {
    runGuarded(() => {
      regenerateRecoveryCodes.mutate(undefined, {
        onSuccess: () => setShowRecoveryCodes(true),
        onError: (error) => {
          if (!guard.retryIfRequired(error, handleRegenerateRecoveryCodes)) {
            notificationHandler.error(
              t(
                'features:account.components.two_factor_status_card.notifications.regenerate_failed',
              ),
            );
          }
        },
      });
    });
  }

  function handleEnrolled() {
    setView('enrolled');
    setShowRecoveryCodes(true);
    notificationHandler.success(
      t(
        isResetting
          ? 'features:account.components.two_factor_status_card.notifications.reset'
          : 'features:account.components.two_factor_status_card.notifications.enabled',
      ),
    );
    setIsResetting(false);
  }

  const isCheckingPasswordStatus = passwordStatus.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <Card.Header headingLevel="h2">
          {t('features:account.components.two_factor_status_card.title')}
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Text variant="secondary">
              {t('features:account.components.two_factor_status_card.description')}
            </Text>

            {twoFactorRequired && view === 'not-enrolled' && (
              <Alert headingLevel="span" variant="warning">
                {t('features:account.components.two_factor_status_card.required_notice')}
              </Alert>
            )}

            {view === 'not-enrolled' && (
              <div className="flex items-center gap-3">
                <Badge>
                  {t('features:account.components.two_factor_status_card.not_enrolled.status')}
                </Badge>
                <Button
                  onClick={handleEnableClick}
                  loading={enableTwoFactor.isPending}
                  disabled={isCheckingPasswordStatus}
                >
                  {t('features:account.components.two_factor_status_card.not_enrolled.action')}
                </Button>
              </div>
            )}

            {view === 'enrolling' && isResetting && (
              <Alert headingLevel="span" variant="warning">
                {t('features:account.components.two_factor_status_card.reset_in_progress_notice')}
              </Alert>
            )}

            {view === 'enrolling' && (
              <EnableTwoFactorFlow
                guard={guard}
                onConfirmed={handleEnrolled}
                onCancel={handleCancelEnrolling}
              />
            )}

            {view === 'enrolled' && (
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="green">
                  {t('features:account.components.two_factor_status_card.enrolled.status')}
                </Badge>
                <Button
                  variant="secondary"
                  onClick={handleRevealRecoveryCodes}
                  disabled={isCheckingPasswordStatus}
                >
                  {t(
                    'features:account.components.two_factor_status_card.enrolled.view_recovery_codes',
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleRegenerateRecoveryCodes}
                  loading={regenerateRecoveryCodes.isPending}
                  disabled={isCheckingPasswordStatus}
                >
                  {t(
                    'features:account.components.two_factor_status_card.enrolled.regenerate_recovery_codes',
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleResetClick}
                  loading={resetTwoFactor.isPending}
                  disabled={isCheckingPasswordStatus}
                >
                  {t('features:account.components.two_factor_status_card.enrolled.action')}
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {view === 'enrolled' && showRecoveryCodes && recoveryCodes.data && (
        <RecoveryCodesCard codes={recoveryCodes.data} />
      )}

      {/* Without this the card simply never appears and the button looks inert. */}
      {view === 'enrolled' && showRecoveryCodes && recoveryCodes.isError && (
        <Alert headingLevel="span" variant="error">
          <div className="flex flex-col items-start gap-2">
            {t('features:account.components.two_factor_status_card.enrolled.recovery_codes_failed')}
            <Button
              variant="secondary"
              onClick={handleRetryRecoveryCodes}
              loading={recoveryCodes.isFetching}
            >
              {t('features:account.components.two_factor_status_card.enrolled.retry')}
            </Button>
          </div>
        </Alert>
      )}

      <ConfirmPasswordModal
        isOpen={guard.isModalOpen}
        onClose={guard.closeModal}
        onConfirmed={guard.resolve}
      />
    </div>
  );
}
